import {
  ILine,
  IMarker,
  IPoint,
  ISize,
  IStrokeOptions,
  PowerResourceType,
} from '@/contract/map.base'

/**
 * 地图观看模式
 */
export enum MapViewMode {
  Init = 'init', // 地图初始状态
  Recommend = 'recommend', // 查看路线模式
  RecommendRouteDetail = 'recommendRouteDetail', // 某条路线详情模式
  EnergyPlan = 'energyPlan', // 路径规划模式
  WishPower = 'wishPower', // 心愿站模式
  NewStation = 'newStation', // 新站上新模式
  CountyLayout = 'countyLayout', // 县县通模式
}

/**
 * 缩放地图模式
 */
export enum ZoomMode {
  Country = 'country', // 全国模式
  Route = 'route', // 线路模式
  City = 'city', // 城市模式
}

/**
 * 配置项
 */
export interface ConfigItem {
  /**
   * 配置key
   */
  key: string
  /**
   * 配置value
   */
  value: string
}

/**
 * 尺寸
 */
export interface Size extends ISize {
  /**
   * 宽度
   */
  width: number
  /**
   * 高度
   */
  height: number
}

/**
 * 地理坐标点
 */
export interface Point extends IPoint {
  /**
   * 点的id
   */
  id?: string
  /**
   * 地理经度
   */
  lng: number
  /**
   * 地理纬度
   */
  lat: number
  /**
   * 点的类型
   */
  type?: string
}

/**
 * 小气泡坐标点及信息
 */
export interface BubbleDestPoint extends Point {
  /**
   * 点的名称
   */
  name: string
  /**
   * 点的地址
   */
  address?: string
}

/**
 * 地理位置
 */
export interface Loc {
  /**
   * ID
   */
  id: string
  /**
   * 名称
   */
  name: string
  /**
   * 行政区划码
   */
  code: string
  /**
   * 经度
   */
  lng: number
  /**
   * 纬度
   */
  lat: number
  /**
   * 地址
   */
  address: string
  /**
   * 省
   */
  province: string
  /**
   * 市
   */
  city: string
  /**
   * 区
   */
  district: string
  /**
   * 街道
   */
  town: string
  /**
   * 村庄
   */
  village: string
  /**
   * 道路
   */
  road: string
  /**
   * 可能是资源点
   */
  type?: string

  /**
   * 资源点类型 20231221新增
   * RECHARGE :recharge 充换电资源 NIO_STORE :nio_store 蔚来门店资源 TEST_DRIVE :test_drive 试驾点资源
   */
  pointType?: string

  /**
   * 资源点子类型 20231221新增
   * POWER_SWAP :PowerSwap 换电站资源 CHARGE_STATION :ChargeStation 充电站资源 NIO_SPACE :NioSpace 蔚来空间 NIO_HOUSE :NioHouse 蔚来中心 TEST_DRIVE :TestDrive 试驾点
   */
  pointSubType?: string
}

/**
 * 地理坐标的矩形区域
 */
export interface Bounds {
  /**
   * 地理坐标矩形区域的西南角
   */
  sw: Point
  /**
   * 地理坐标矩形区域的东北角
   */
  ne: Point
}

/**
 * 地面叠加层选项
 */
export interface GroundOverlayOptions {
  /**
   * 地面叠加层类型
   * image: 图片
   * canvas: 画布
   * video: 视频
   */
  type: string
  /**
   * 地面叠加层图片地址
   */
  url: string
  /**
   * 地面叠加层透明度
   */
  opacity: number
  /**
   * 地面叠加层级
   */
  zIndex: number
}

/**
 * 地图标注类型
 */
export enum MarkerType {
  /**
   * 图标类标注
   */
  Icon = 'Icon',
  /**
   * 文本类标注
   */
  Text = 'Text',
  /**
   * 聚合类标注
   */
  Merged = 'Merged',
  /**
   * 混合类标注
   */
  Mixed = 'Mixed',
  /**
   * 标题类标注
   */
  Title = 'Title',
  /**
   * 标题类标注
   */
  Animation = 'Animation',
}

/**
 * 地图标注
 */
export interface Marker extends IMarker {
  /**
   * 地图标注id
   */
  id: string
  /**
   * 地图标注类型
   */
  type: MarkerType
  /**
   * 地图标注位置
   */
  loc: Point
  /**
   * 地图标注icon
   */
  icon: string
  /**
   * 地图标注标题
   */
  title?: string
  /**
   * 地图标注标签
   */
  label?: string
  /**
   * 地图标注是否可点击
   */
  clickable?: boolean
  /**
   * marker是否可根据zoom缩放
   */
  zoomable?: boolean
  /**
   * 地图标注尺寸
   */
  size?: Size
  /**
   * 地图标注的层级
   */
  zIndex?: number
  /**
   * 地图标注携带的数据
   */
  data?: any
  /**
   * 换电站正在换电状态
   * 0表示空闲状态，1表示换电状态
   */
  swapStatus?: number
  /**
   * 自定义参数
   */
  customData?: any
}

/**
 * 地图线路绘制选项
 */
export interface StrokeOptions extends IStrokeOptions {
  /**
   * 地图线路颜色
   */
  strokeColor?: string
  /**
   * 地图线路宽度
   */
  strokeWeight?: number
  /**
   * 地图线路透明度
   */
  strokeOpacity?: number
}

/**
 * 地图填充绘制选项
 */
export interface FillOptions {
  /**
   * 地图填充颜色
   */
  fillColor?: string
  /**
   * 地图线路透明度
   */
  fillOpacity?: number
}

export interface drawOptions extends StrokeOptions, FillOptions {}

/**
 * 地图线路
 */
export interface Line extends ILine {
  /**
   * 路线id
   */
  id?: string
  /**
   * 地图线路点
   */
  points: Point[]
  /**
   * 地图线路绘制选项
   */
  options?: drawOptions
  /**
   * 地图线路是否可点击
   */
  clickable?: boolean
  /**
   * 不可被clearOverlays清除
   */
  disableMassClear?: boolean
}

/**
 * 省市信息
 */
export interface AreaInfo {
  /**
   * 省市名称
   */
  name: string
  /**
   * 省市简称
   */
  simple: string
  /**
   * 省包含的城市列表
   */
  cities?: AreaInfo[]

  /**
   * 城市的区
   */
  districts?: AreaInfo[]
}

/**
 * 城市信息
 */
export interface CityInfo {
  /**
   * 城市名称
   */
  name: string
  /**
   * 城市行政区划码
   */
  code: string
  /**
   * 城市地理位置
   */
  loc: Point
  /**
   * 城市简称
   */
  simple?: string
  /**
   * 默认或兜底的zoom level
   */
  zoom?: number
  /**
   * 是否使用默认的zoom （新疆那些大省份需要使用默认）
   */
  useDefaultZoom?: boolean
  /**
   * 是否使用几何中心
   */
  useGeometricCenter?: boolean
}

/**
 * 加电路线
 */
export interface PowerRoute {
  /**
   * 路线ID
   */
  id: string
  /**
   * 路线描述
   */
  description: string
  /**
   * 路线
   */
  polyline: Point[]
  /**
   * 路径起始点以及终点位置信息
   */
  locations: Loc[]
  /**
   * 标签信息
   */
  tags: string[]
  /**
   * 加电资源
   */
  resources: PowerResource[]
  /**
   * 路线详情
   */
  detail?: any
  /**
   * 加电线路资源点距上一个的距离
   */
  distance?: number[]
}

/**
 * 加电资源
 */
export interface PowerResource {
  /**
   * 加电资源id
   */
  id: string

  /**
   * 加电资源名称
   */
  name: string

  /**
   * 加电资源是否可点击
   */
  clickAvailable: boolean

  /**
   * 资源点类型 20231221新增
   * RECHARGE :recharge 充换电资源 NIO_STORE :nio_store 蔚来门店资源 TEST_DRIVE :test_drive 试驾点资源
   */
  pointType: string

  /**
   * 资源点子类型 20231221新增
   * POWER_SWAP :PowerSwap 换电站资源 CHARGE_STATION :ChargeStation 充电站资源 NIO_SPACE :NioSpace 蔚来空间 NIO_HOUSE :NioHouse 蔚来中心 TEST_DRIVE :TestDrive 试驾点
   */
  pointSubType: string

  /**
   * 加电资源类别：ChargeStation / PowerSwap / Merged，其中 Merged 表示字段是 merge 的结果
   */
  type: string

  /**
   * 桩群类型。仅type为ChargeStation时有该字段
   * NioChargeStationNpc: nio超充桩
   * NioChargeStationDest: nio目的地充电桩
   * ThirdChargeStation: 三方桩
   */
  chargeStationType: string

  /**
   * 聚合点资源类型：ChargeStation / PowerSwap / Mix
   */
  mergeType: string

  /**
   * 加电资源位置。type 为 Merged 的 时候为 Merged 中心点位置
   */
  location: string

  /**
   * 运营商id
   */
  operatorId: string

  /**
   * 运营商名称
   */
  operatorName: string

  /**
   * 建设场所：
   * 1: 居民区
   * 2: 公共机构
   * 3: 企事业单位
   * 4: 写字楼
   * 5: 工业园区
   * 6: 交通枢纽
   * 7: 大型文体设施
   * 8: 城市绿地
   * 9: 大型建筑配建停车场
   * 10: 路边停车位
   * 11: 城际高速服务区
   * 201: 酒店民宿
   * 202: 景区乐园
   * 203: 商业中心
   * 255: 其他
   */
  construction: string

  /**
   * 地址
   */
  address: string

  /**
   * 资源merge之后的总数
   */
  resourceNumber: number

  /**
   * 权益概述
   */
  rightDescSummary: number

  /**
   * 权益详细描述
   */
  rightDescDetailArray: string[]

  /**
   * 资源点在路线中的位置索引
   */
  index: number[]

  /**
   * lng坐标
   */
  lng?: number

  /**
   * lat坐标
   */
  lat?: number

  /**
   * 换电站正在换电状态
   * 0表示空闲状态，1表示换电状态
   */
  swapStatus?: number

  /**
   * 「换电站当前是否支持相关品牌换电」0:不支持1:支持2.升级后支持(有可能存在为null的情况,当前需求遇null可忽略)
   */
  swapSupportStatus?: number
}

// /**
//  * 加电资源类型
//  */
// export enum PowerResourceType {
//   /**
//    * 换电站
//    */
//   PowerSwap = 'PowerSwap',
//   /**
//    * nio充电站
//    */
//   ChargeStation = 'NioChargeStationNpc;NioChargeStationDest',
//   /**
//    * nio超充桩
//    */
//   NioChargeStationNpc = 'NioChargeStationNpc',
//   /**
//    * nio目的地充电桩
//    */
//   NioChargeStationDest = 'NioChargeStationDest',
//   /**
//    * 三方桩
//    */
//   ThirdChargeStation = 'ThirdChargeStation',
//   /**
//    * 蔚来空间
//    */
//   NioSpace = 'nio_space',
//   /**
//    * 蔚来中心
//    */
//   NioHouse = 'nio_house',
//   /**
//    * 蔚来路演
//    */
//   NioRoadShow = 'nio_road_show',
//   /**
//    * 试驾点
//    */
//   TestDrive = 'test_drive',
//   /**
//    * 聚合资源
//    */
//   Merged = 'Merged',
//   /**
//    * 未知
//    */
//   Unknown = 'Unknown',
// }

/**
 * 搜索列表大类
 */
export enum PointType {
  /**
   * 资源
   */
  Recharge = 'recharge',
  /**
   * 换电资源
   */
  RechargePS = 'recharge|ps',
  /**
   * 充电资源
   */
  RechargeCS = 'recharge|cs',
  /**
   * 蔚来门店
   */
  NioStore = 'nio_store',
  /**
   * 试驾点
   */
  TestDrive = 'test_drive',
  /**
   * 服务中心
   */
  ServiceCenter = 'service_center',
  /**
   * 地点
   */
  Poi = 'poi',
}

export enum FilterState {
  /**
   * 筛选开关打开，在地图上展示该类型的加电资源
   */
  On = 'On',
  /**
   * 筛选开关关闭，在地图上展示该类型的加电资源
   */
  Off = 'Off',
  /**
   * 禁用开关且不在地图上展示该类型的加电资源
   */
  Disabled = 'Disabled',
}

/**
 * 加电资源筛选项
 */
export interface PowerResourceFilterItem {
  /**
   * 名称
   */
  name: string
  /**
   * 加电资源类型
   * PowerSwap: 换电站
   * NioChargeStationNpc: nio超充桩
   * NioChargeStationDest: nio目的地充电桩
   * ThirdChargeStation: 三方桩
   */
  resourceType: PowerResourceType
  /**
   * 筛选开关
   */
  filterState: FilterState
}

/**
 * 加电资源筛选条件
 */
export interface PowerResourceFilter {
  /**
   * 加电资源筛选项
   */
  items: PowerResourceFilterItem[]
}

/**
 * 加电资源数据概览
 */
export interface PowerResourceSummary {
  /**
   * 统计范围，city--城市级别，country--国家级别
   */
  statisticRange: string //

  /**
   * 换电站数量
   */
  swapNumber: string //

  /**
   * 超充桩群数量
   */
  nioNpcChargerNumber: string //

  /**
   * 超充枪数量
   */
  nioNpcConnectorNumber: string //

  /**
   * 目充桩群数量
   */
  nioDestChargerNumber: string //

  /**
   * 目充枪数量
   */
  nioDestConnectorNumber: string //

  /**
   * 三方枪数量
   */
  thirdConnectorNumber: string //

  /**
   * 电区房覆盖率
   */
  psDistrictHousingCoveredRate: string //

  /**
   * 更新时间
   */
  statisticUpdateTime: string //

  /**
   * 高速换电站数量，仅country级别有
   */
  highSpeedSwapNumber: string //

  /**
   * nio桩群总数
   */
  nioChargerNumber: string //

  /**
   * nio枪总数
   */
  nioConnectorNumber: string //

  /**
   * 累计换电次数
   */
  totalSwapTimesNumber: string //

  /**
   * 高速公路充电站 座
   */
  highSpeedNioChargerNumber: string

  /**
   * 高速公路充电站 根
   */
  highSpeedNioConnectorNumber: string
}

/**
 * 路径规划起点终点
 */
export interface EnergyPlanLocation {
  /**
   * 起点
   */
  orig: string
  /**
   * 终点
   */
  dest: string
}

/**
 * 路径规划plan.route.step
 */
export interface StepItem {
  /**
   * step数据
   */
  polyline: string
  distance: number
  status: number
}

/**
 * 路径规划plan.route
 */
export interface PlanRoute {
  /**
   * step
   */
  steps: StepItem[]
}

/**
 * 路径规划plan.resources
 */
export interface EnergyPlanPowerResource {
  /**
   * 是否是候选点
   */
  isCandidate: boolean
  /**
   * 距离前一个候选点的距离
   */
  prevCandidateDistance: number
  /**
   * 资源点
   */
  resource: PowerResource
}

/**
 * 路径规划plan
 */
export interface Plan {
  /**
   * 起点
   */
  route: PlanRoute
  /**
   * 加电资源
   */
  resources: EnergyPlanPowerResource[]
}

/**
 * 路径规划信息
 */
export interface EnergyPlan {
  /**
   * 起点和终点
   */
  locations: EnergyPlanLocation
  /**
   * 路径规划
   */
  plan: Plan
}

/**
 * 地图状态信息
 */
export interface MapState {
  /**
   * 地图中心点
   */
  center: Point
  /**
   * 地图中心位置
   */
  centerLocation?: Loc
  /**
   * 地图当前选中的位置
   */
  currentLocation?: Loc
  /**
   * 地图缩放等级
   */
  zoomLevel: number
  /**
   * 地图最小缩放等级
   */
  minZoomLevel: number
  /**
   * 地图最大缩放等级
   */
  maxZoomLevel: number
  /**
   * 加电资源
   */
  resources: PowerResource[]
  /**
   * 加电资源筛选条件
   */
  filter: PowerResourceFilter
  /**
   * 是否允许循转
   */
  enableRotateGestures?: boolean
}

/**
 * 地图历史状态信息
 */
export interface MapHistoryState {
  /**
   * 地图中心点
   */
  center: Point
  /**
   * 地图缩放等级
   */
  zoomLevel: number
  /**
   * 用户选中的目标点
   */
  dest?: Point
}

/**
 * 地理位置
 */
export interface ILocation {
  /**
   * ID
   */
  id: string
  /**
   * 名称
   */
  name: string
  /**
   * 行政区划码
   */
  code: string
  /**
   * 经度
   */
  lng: number
  /**
   * 纬度
   */
  lat: number
  /**
   * 地址
   */
  address: string
  /**
   * 省
   */
  province: string
  /**
   * 市
   */
  city: string
  /**
   * 区
   */
  district: string
  /**
   * 街道
   */
  town: string
  /**
   * 村庄
   */
  village: string
  /**
   * 道路
   */
  road: string
  /**
   * 可能是资源点
   */
  type?: string

  /**
   * 资源点类型 20231221新增
   * RECHARGE :recharge 充换电资源 NIO_STORE :nio_store 蔚来门店资源 TEST_DRIVE :test_drive 试驾点资源
   */
  pointType?: string

  /**
   * 资源点子类型 20231221新增
   * POWER_SWAP :PowerSwap 换电站资源 CHARGE_STATION :ChargeStation 充电站资源 NIO_SPACE :NioSpace 蔚来空间 NIO_HOUSE :NioHouse 蔚来中心 TEST_DRIVE :TestDrive 试驾点
   */
  pointSubType?: string
}
